import { motion } from "framer-motion";
import {
  UserRound,
  Settings2,
  ClipboardCheck,
  ClipboardList,
  FileText,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

const SS = "/screenshots";
const WORKFLOW_SS = "/screenshots/workflow";
const WEBSITE_V2_SS = "/screenshots/website-v2";

const RSA_ASSESSMENT = `${SS}/hero-live-assessment-rsa.jpg`;
const DVSA_ASSESSMENT = `${SS}/hero-live-assessment-dvsa.jpg`;
const TEST_SUMMARY = `${WORKFLOW_SS}/workflow-04-summary.jpg`;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const IrelandFlag = () => (
  <svg viewBox="0 0 36 24" aria-label="Ireland" className="h-4 w-6 rounded-[3px] shadow-sm">
    <rect width="12" height="24" fill="#169B62" />
    <rect x="12" width="12" height="24" fill="#fff" />
    <rect x="24" width="12" height="24" fill="#FF883E" />
  </svg>
);

const UKFlag = () => (
  <svg
    viewBox="0 0 60 30"
    aria-label="United Kingdom"
    className="h-4 w-8 rounded-[3px] shadow-sm"
  >
    <clipPath id="uk-flag-clip">
      <path d="M0 0h60v30H0z" />
    </clipPath>
    <g clipPath="url(#uk-flag-clip)">
      <path d="M0 0h60v30H0z" fill="#012169" />
      <path d="m0 0 60 30m0-30L0 30" stroke="#fff" strokeWidth="6" />
      <path d="m0 0 60 30m0-30L0 30" stroke="#C8102E" strokeWidth="4" />
      <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10" />
      <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6" />
    </g>
  </svg>
);

const reportHighlights = [
  {
    title: "Assessment reports",
    description:
      "Create professional assessment reports featuring your own instructor or driving school branding.",
    image: `${WEBSITE_V2_SS}/hero-assessment-report.png`,
    alt: "MockTP branded assessment report preview",
  },
  {
    title: "Progress summaries",
    description:
      "Share student progress summaries with a clear, branded record of development over time.",
    image: `${WEBSITE_V2_SS}/student-progress-summary-report.png`,
    alt: "MockTP student progress summary report preview",
  },
];

const workflowModes = [
  {
    title: "Live Workflow",
    description:
      "Where appropriate, instructors can record progress directly in MockTP while following local laws, regulations and professional guidance.",
    Flag: IrelandFlag,
    visual: "rsa",
  },
  {
    title: "A practical workflow for UK instructors",
    description:
      "Record observations using your usual paper-based method during the drive. Once safely parked, enter the recorded information into MockTP. Entering your notes typically takes just 30–60 seconds, while still creating the same professional reports, student history and progress insights.",
    Flag: UKFlag,
    visual: "uk",
  },
];

const workflowSteps = [
  {
    number: "01",
    label: "Student",
    Icon: UserRound,
    title: "Start with your student",
    description: "Choose an existing student or create a new one in seconds.",
    image: `${WORKFLOW_SS}/workflow-01-student.jpg`,
    alt: "MockTP student selection screen",
  },
  {
    number: "02",
    label: "Test Setup",
    Icon: Settings2,
    title: "Set up the assessment",
    description:
      "Add the key test details before starting, including vehicle, transmission, weather, route and notes.",
    image: `${WORKFLOW_SS}/workflow-02-test_setup.jpg`,
    alt: "MockTP test setup screen with assessment details",
  },
  {
    number: "03",
    label: "Assessment",
    Icon: ClipboardCheck,
    title: "Complete the assessment",
    description:
      "Whether recorded live or entered afterwards, every assessment follows the same professional workflow.",
    image: `${WORKFLOW_SS}/workflow-03-assessment.jpg`,
    alt: "MockTP live assessment screen",
    featured: true,
  },
  {
    number: "04",
    label: "Summary",
    Icon: ClipboardList,
    title: "Review the results",
    description: "Review and edit recorded marks before generating the final professional report.",
    image: `${WORKFLOW_SS}/workflow-04-summary.jpg`,
    alt: "MockTP assessment summary screen",
    scroll: true,
  },
  {
    number: "05",
    label: "Assessment Report",
    Icon: FileText,
    title: "Share the assessment report",
    description:
      "Email or WhatsApp a professional assessment report featuring your own logo, giving students a clear record of their lesson.",
    image: `${WEBSITE_V2_SS}/hero-assessment-report.png`,
    alt: "MockTP completed assessment report",
    report: true,
  },
  {
    number: "06",
    label: "Student Progress",
    Icon: TrendingUp,
    title: "Continue where you left off",
    description:
      "Open Student Progress at the next lesson to review previous assessments and monitor improvement over time.",
    image: `${WORKFLOW_SS}/workflow-06-progress_app.jpg`,
    alt: "MockTP student progress screen",
    scroll: true,
  },
  {
    number: "07",
    label: "Progress Report",
    Icon: FileText,
    title: "Share the progress report",
    description:
      "Create a clear progress report showing completed assessments, pass rate and development over time.",
    image: `${WEBSITE_V2_SS}/student-progress-summary-report.png`,
    alt: "MockTP student progress PDF report",
    report: true,
  },
];

const Eyebrow = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#00DC82]">
    {children}
  </span>
);

const Heading = ({ children, testid }) => (
  <h2
    data-testid={testid}
    className="text-3xl sm:text-4xl font-bold tracking-tighter text-white leading-[1.12]"
  >
    {children}
  </h2>
);

const Body = ({ children }) => (
  <p className="text-base md:text-lg text-slate-400 leading-relaxed max-w-xl">{children}</p>
);

const ClipboardFrame = () => (
  <div className="relative mx-auto w-full max-w-[185px] rounded-[16px] border border-white/[0.12] bg-[#151A24] p-2 shadow-[0_24px_70px_-34px_rgba(0,0,0,0.95)]">
    <div className="absolute left-1/2 top-1.5 z-20 h-5 w-16 -translate-x-1/2 rounded-b-xl rounded-t-md border border-white/15 bg-gradient-to-b from-slate-200 to-slate-500 shadow-[0_8px_18px_rgba(0,0,0,0.35)]" />
    <div className="overflow-hidden rounded-lg bg-white p-2 pt-6">
      <img
        src={`${WEBSITE_V2_SS}/dl25-driving-test-report.png`}
        alt="UK DL25 driving test report marking sheet"
        loading="lazy"
        decoding="async"
        className="block h-auto w-full rounded-md object-contain"
      />
    </div>
  </div>
);

const PhonePreview = ({ src, alt, className = "max-w-[190px]" }) => (
  <div
    className={`relative mx-auto w-full ${className} rounded-[26px] border border-[#00DC82]/35 bg-[#050914] p-1.5 shadow-[0_24px_70px_-34px_rgba(0,220,130,0.55)]`}
  >
    <div className="pointer-events-none absolute left-1/2 top-1.5 z-20 h-1 w-12 -translate-x-1/2 rounded-full bg-black/70" />
    <div className="overflow-hidden rounded-[21px] bg-[#07101D]">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="block h-auto w-full object-contain"
      />
    </div>
  </div>
);

const UKWorkflowMorph = () => (
  <div className="uk-morph-stage relative h-[320px] w-[205px] sm:h-[340px]">
    <div className="uk-morph-layer uk-morph-paper absolute inset-0 flex items-center justify-center">
      <ClipboardFrame />
    </div>
    <div className="uk-morph-layer uk-morph-phone absolute inset-0 flex items-center justify-center">
      <PhonePreview
        src={DVSA_ASSESSMENT}
        alt="MockTP DVSA assessment screen"
        className="max-w-[140px]"
      />
    </div>
    <div
      className="uk-capture-sweep pointer-events-none absolute inset-x-0 top-0 z-10 mx-auto h-12 max-w-[175px]"
      aria-hidden="true"
    />
  </div>
);

const WorkflowVisualLabel = ({ children }) => (
  <p className="mb-1 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-[#00DC82]">
    {children}
  </p>
);

const RSAWorkflowVisual = () => (
  <div className="mt-6 flex flex-col items-center rounded-2xl border border-white/[0.07] bg-[#050914]/45 p-4">
    <WorkflowVisualLabel>RSA Assessment</WorkflowVisualLabel>
    <p className="mb-5 text-center text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
      Ireland · Live in MockTP
    </p>
    <PhonePreview
      src={RSA_ASSESSMENT}
      alt="MockTP RSA live assessment screen for Ireland"
      className="max-w-[220px]"
    />
  </div>
);

const WorkflowArrow = () => (
  <ArrowRight
    className="my-1 h-5 w-5 shrink-0 rotate-90 text-[#00DC82] sm:my-0 sm:rotate-0"
    strokeWidth={2.5}
    aria-hidden="true"
  />
);

const WorkflowStageCaption = ({ children }) => (
  <p className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
    {children}
  </p>
);

const UKWorkflowVisual = () => (
  <div className="mt-6 rounded-2xl border border-white/[0.07] bg-[#050914]/45 p-4">
    <WorkflowVisualLabel>DVSA Workflow</WorkflowVisualLabel>
    <p className="mb-5 text-center text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
      Great Britain · Paper to MockTP
    </p>

    <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3">
      {/* DL25 Paper -> MockTP Assessment (morph) */}
      <div className="flex flex-col items-center">
        <UKWorkflowMorph />
        <WorkflowStageCaption>DL25 Paper → MockTP</WorkflowStageCaption>
      </div>

      <WorkflowArrow />

      {/* Immediate Test Summary (genuine MockTP screen) */}
      <div className="flex flex-col items-center">
        <div className="flex h-[320px] items-center sm:h-[340px]">
          <PhonePreview
            src={TEST_SUMMARY}
            alt="MockTP test summary screen"
            className="max-w-[145px]"
          />
        </div>
        <WorkflowStageCaption>Test Summary</WorkflowStageCaption>
      </div>
    </div>
  </div>
);

const ProfessionalReportsBranding = () => (
  <section
    id="reports"
    data-testid="reports-section"
    className="relative overflow-hidden scroll-mt-24"
  >
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute left-1/2 top-1/2 h-[520px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00DC82]/[0.06] blur-[150px]" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
        className="max-w-3xl mx-auto text-center flex flex-col items-center gap-5"
      >
        <Eyebrow>Reports & Branding</Eyebrow>
        <Heading testid="reports-title">Professional Reports & Branding</Heading>
        <Body>
          <span className="block mx-auto">
            Create professional assessment and student progress reports featuring your
            own instructor or driving school branding, delivering a polished experience
            for every student.
          </span>
        </Body>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-2"
      >
        {reportHighlights.map((item) => (
          <article
            key={item.title}
            className="overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.03] transition-[transform,box-shadow,border-color,background-color] duration-300 hover:-translate-y-1.5 hover:border-white/15 hover:bg-white/[0.05] hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]"
          >
            <div className="border-b border-white/[0.07] bg-[#050914]/80 p-4 sm:p-5">
              <div className="flex items-center justify-center overflow-hidden rounded-xl bg-white p-2 sm:p-3">
                <img
                  src={item.image}
                  alt={item.alt}
                  loading="lazy"
                  decoding="async"
                  className="block h-auto w-full object-contain"
                />
              </div>
            </div>
            <div className="p-7">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-white/[0.05] text-[#00DC82]">
                <FileText size={24} strokeWidth={1.75} />
              </div>
              <h3 className="text-lg font-semibold tracking-tight text-white">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.description}</p>
            </div>
          </article>
        ))}
      </motion.div>
    </div>
  </section>
);

const ScrollingPhoneScreen = ({ src, alt }) => (
  <>
    <div className="workflow-scroll-viewport absolute inset-x-0 overflow-hidden bg-[#07101D]">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="workflow-scroll-image block w-full h-auto"
      />
    </div>
    <div className="workflow-fixed-status absolute inset-x-0 top-0 z-20 overflow-hidden bg-[#07101D]">
      <img src={src} alt="" aria-hidden="true" loading="lazy" className="block w-full h-auto" />
    </div>
    <div className="workflow-fixed-bottom absolute inset-x-0 bottom-0 z-20 overflow-hidden bg-[#07101D]">
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-x-0 bottom-0 block w-full h-auto"
      />
    </div>
  </>
);

const WorkflowPhone = ({ step }) => {
  const phoneWidth = step.featured ? "w-[252px] sm:w-[274px]" : "w-[224px] sm:w-[244px]";
  const screenHeight = step.featured ? "h-[530px]" : "h-[470px]";
  const screenHeightValue = step.featured ? "530px" : "470px";
  const screenStyle = step.scroll
    ? {
        "--workflow-screen-height": screenHeightValue,
        "--workflow-fixed-top": "32px",
        "--workflow-fixed-bottom": "56px",
        "--workflow-scroll-window-height": `calc(${screenHeightValue} - 88px)`,
      }
    : { "--workflow-screen-height": screenHeightValue };

  return (
    <div
      className={`relative mx-auto ${phoneWidth} rounded-[32px] border bg-[#050914] p-2 shadow-[0_34px_90px_-34px_rgba(0,220,130,0.55)] ring-1 ${
        step.scroll || step.featured
          ? "border-[#00DC82]/45 ring-[#00DC82]/25"
          : "border-white/10 ring-white/10"
      }`}
    >
      <div className="pointer-events-none absolute left-1/2 top-2 h-1.5 w-16 -translate-x-1/2 rounded-full bg-black/70 z-30" />
      <div
        className={`workflow-phone-screen relative ${screenHeight} overflow-hidden rounded-[25px] bg-[#07101D]`}
        style={screenStyle}
      >
        {step.scroll ? (
          <ScrollingPhoneScreen src={step.image} alt={step.alt} />
        ) : (
          <img
            src={step.image}
            alt={step.alt}
            loading="lazy"
            className="block w-full h-full object-cover object-top"
          />
        )}
      </div>
    </div>
  );
};

const WorkflowReport = ({ step }) => (
  <div className="relative mx-auto w-full max-w-[620px] rounded-[24px] border border-white/15 bg-white p-3 shadow-[0_44px_130px_-38px_rgba(0,220,130,0.55)] ring-1 ring-[#00DC82]/20 sm:p-4 lg:w-[130%] lg:max-w-[800px]">
    <img
      src={step.image}
      alt={step.alt}
      loading="lazy"
      className="block w-full rounded-[17px] object-contain object-top"
    />
  </div>
);

const WorkflowVisual = ({ step }) => (
  <div
    className={`relative flex justify-center lg:items-center ${
      step.report ? "lg:min-h-[760px]" : "lg:min-h-[520px]"
    }`}
  >
    <div className="pointer-events-none absolute inset-x-10 top-1/2 h-32 -translate-y-1/2 rounded-full bg-[#00DC82]/10 blur-[70px]" />
    <div className="relative z-10 w-full">{step.report ? <WorkflowReport step={step} /> : <WorkflowPhone step={step} />}</div>
  </div>
);

const WorkflowStep = ({ step, index }) => {
  const Icon = step.Icon;
  const isEven = index % 2 === 1;
  const copyPosition = isEven ? "lg:col-start-3" : "lg:col-start-1";
  const visualPosition = isEven ? "lg:col-start-1" : "lg:col-start-3";

  const copy = (
    <div className="relative rounded-lg border border-white/10 bg-white/[0.035] p-5 sm:p-6 shadow-[0_24px_80px_-48px_rgba(0,0,0,0.9)]">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#00DC82]/25 bg-[#00DC82]/10 text-[#00DC82]">
          <Icon size={20} strokeWidth={2} />
        </span>
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#00DC82]">
          Step {step.number} / {step.label}
        </span>
      </div>
      <h3 className="mt-5 text-xl sm:text-2xl font-semibold tracking-tight text-white">
        {step.title}
      </h3>
      <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-400">
        {step.description}
      </p>
    </div>
  );

  const visual = <WorkflowVisual step={step} />;

  return (
    <li className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_96px_minmax(0,1fr)] lg:items-center">
      <div className={copyPosition}>{copy}</div>
      <div className="relative hidden lg:flex lg:col-start-2 lg:row-start-1 justify-center self-stretch">
        <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/15" />
        <div className="relative mt-6 flex h-12 w-12 items-center justify-center rounded-full border border-[#00DC82]/30 bg-[#08111F] text-sm font-bold text-[#00DC82] shadow-[0_0_40px_rgba(0,220,130,0.18)]">
          {step.number}
        </div>
      </div>
      <div className={`lg:row-start-1 ${visualPosition}`}>{visual}</div>
    </li>
  );
};

const WorkflowSection = () => (
  <section
    id="workflow"
    data-testid="workflow-section"
    aria-labelledby="workflow-title"
    className="relative overflow-hidden scroll-mt-24"
  >
    <style>{`
      @keyframes workflowPhoneScroll {
        0%, 14% { transform: translateY(calc(-1 * var(--workflow-fixed-top))); }
        54%, 72% { transform: translateY(calc(-100% + var(--workflow-scroll-window-height) + var(--workflow-fixed-bottom))); }
        100% { transform: translateY(calc(-1 * var(--workflow-fixed-top))); }
      }

      @keyframes workflowLineReveal {
        from { transform: scaleY(0); }
        to { transform: scaleY(1); }
      }

      .workflow-scroll-viewport {
        top: var(--workflow-fixed-top);
        bottom: var(--workflow-fixed-bottom);
      }

      .workflow-fixed-status {
        height: var(--workflow-fixed-top);
      }

      .workflow-fixed-bottom {
        height: var(--workflow-fixed-bottom);
      }

      .workflow-scroll-image {
        animation: workflowPhoneScroll 18s ease-in-out infinite;
        transform: translateY(calc(-1 * var(--workflow-fixed-top)));
      }

      .workflow-phone-screen:hover .workflow-scroll-image {
        animation-play-state: paused;
      }

      .workflow-line-fill {
        animation: workflowLineReveal 1.2s ease-out both;
        transform-origin: top;
      }

      @keyframes ukPaperMorph {
        0%, 12%   { opacity: 1; transform: translateY(0) scale(1); }
        38%, 60%  { opacity: 0; transform: translateY(-14px) scale(0.965); }
        86%, 100% { opacity: 1; transform: translateY(0) scale(1); }
      }

      @keyframes ukPhoneMorph {
        0%, 14%   { opacity: 0; transform: translateY(20px) scale(0.94); }
        40%, 62%  { opacity: 1; transform: translateY(0) scale(1); }
        84%, 100% { opacity: 0; transform: translateY(20px) scale(0.94); }
      }

      @keyframes ukCaptureSweep {
        0%, 10%   { opacity: 0; transform: translateY(-56px); }
        16%       { opacity: 0.9; }
        38%       { opacity: 0.65; transform: translateY(360px); }
        44%, 100% { opacity: 0; transform: translateY(360px); }
      }

      .uk-morph-layer {
        will-change: opacity, transform;
      }

      .uk-morph-paper {
        animation: ukPaperMorph 9s ease-in-out infinite;
      }

      .uk-morph-phone {
        animation: ukPhoneMorph 9s ease-in-out infinite;
      }

      .uk-capture-sweep {
        background: linear-gradient(
          to bottom,
          rgba(0, 220, 130, 0) 0%,
          rgba(0, 220, 130, 0.5) 50%,
          rgba(0, 220, 130, 0) 100%
        );
        filter: blur(2px);
        animation: ukCaptureSweep 9s ease-in-out infinite;
      }

      .uk-morph-stage:hover .uk-morph-paper,
      .uk-morph-stage:hover .uk-morph-phone,
      .uk-morph-stage:hover .uk-capture-sweep {
        animation-play-state: paused;
      }

      @media (prefers-reduced-motion: reduce) {
        .workflow-scroll-image,
        .workflow-line-fill,
        .uk-morph-paper,
        .uk-morph-phone,
        .uk-capture-sweep {
          animation: none;
        }

        .uk-morph-paper {
          opacity: 1;
          transform: translate(-42px, -8px) scale(0.9);
        }

        .uk-morph-phone {
          opacity: 1;
          transform: translate(46px, 14px);
        }

        .uk-capture-sweep {
          display: none;
        }
      }
    `}</style>

    <div className="pointer-events-none absolute inset-0">
      <div className="absolute bottom-0 right-1/4 h-[400px] w-[600px] rounded-full bg-[#00DC82]/[0.05] blur-[140px]" />
      <div className="absolute left-1/2 top-1/4 h-[420px] w-[760px] -translate-x-1/2 rounded-full bg-white/[0.025] blur-[150px]" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 pt-36 pb-24 lg:pt-44 lg:pb-32">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
        className="max-w-3xl mx-auto text-center flex flex-col items-center gap-5"
      >
        <Eyebrow>The Workflow</Eyebrow>
        <Heading testid="workflow-title">
          <span id="workflow-title" className="block max-w-[680px] mx-auto">
            Flexible Assessment Workflow
          </span>
        </Heading>
        <Body>
          <span className="block mx-auto">
            See how MockTP fits naturally into every lesson, from choosing a student to
            reviewing progress at the next lesson.
          </span>
        </Body>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-5 lg:grid-cols-2"
      >
        {workflowModes.map((mode) => (
          <article
            key={mode.title}
            className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-6 text-left shadow-[0_24px_80px_-48px_rgba(0,0,0,0.9)]"
          >
            <div className="flex items-center gap-3">
              {mode.Flag && <mode.Flag />}
              <h3 className="text-lg font-semibold tracking-tight text-white">{mode.title}</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{mode.description}</p>
            {mode.visual === "uk" && <UKWorkflowVisual />}
            {mode.visual === "rsa" && <RSAWorkflowVisual />}
          </article>
        ))}
      </motion.div>

      <motion.p
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
        className="mx-auto mt-5 max-w-3xl text-center text-xs leading-relaxed text-slate-500"
      >
        Always follow local laws, regulations and professional guidance regarding device use
        while supervising a learner.
      </motion.p>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.08 }}
        className="relative mt-16 lg:mt-20"
      >
        <div className="workflow-line-fill pointer-events-none absolute left-5 top-0 h-full w-px bg-gradient-to-b from-[#00DC82]/0 via-[#00DC82]/50 to-[#00DC82]/0 lg:left-1/2" />
        <ol className="relative grid gap-16 lg:gap-20 pl-12 lg:pl-0">
          {workflowSteps.map((step, index) => (
            <WorkflowStep key={step.number} step={step} index={index} />
          ))}
        </ol>
      </motion.div>
    </div>
  </section>
);

export const Showcase = () => (
  <>
    <ProfessionalReportsBranding />
    <WorkflowSection />
  </>
);
